import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { TrialBlock } from "@/types/trialBlock"
import { TrialBlockDateRange } from "@/utils/trialBlockUtils"

export const useTrialBlocksFetch = (filters: {
  dateRange: TrialBlockDateRange
  location?: string
  showPendingOnly: boolean
}) => {
  return useQuery({
    queryKey: ["trialBlocks", filters],
    queryFn: async () => {
      // Filtering on the depot only drops rows when every embed up to it is an inner join
      const join = filters.location ? "!inner" : ""
      let query = supabase
        .from("trial_blocks")
        .select(
          `
          *,
          application:application_id${join}(
            id,
            applicant:applicant_id(first_name, last_name),
            job:job_id${join}(title, depot:depot_id(id, city))
          )
        `,
        )
        .gte("date", filters.dateRange.from)
        .lte("date", filters.dateRange.to)

      if (filters.location) {
        query = query.eq("application.job.depot_id", filters.location)
      }

      // A block with no review_status is shown as pending
      if (filters.showPendingOnly) {
        query = query.or('review_status.is.null,review_status.eq.pending,review_status.eq.""')
      }

      const { data, error } = await query

      if (error) {
        throw new Error(`Error fetching trial blocks: ${error.message}`)
      }
      // Transform data to match our TrialBlock interface
      const transformedData: TrialBlock[] = data.map((item) => ({
        id: item.id,
        driverName:
          item.application?.applicant?.first_name + " " + item.application?.applicant?.last_name ||
          "",
        location: item.application?.job?.depot?.city || "",
        depotId: item.application?.job?.depot?.id || "",
        reviewStatus: (item.review_status as TrialBlock["reviewStatus"]) || "pending",
        vehicleName: item.vehicle_name || "",
        result: item.result || "",
        trialId: item.trial_id || "",
        position: item.application?.job?.title || "",
        routeCode: item.route_code || "",
        routeCycle: item.route_cycle || "",
        date: item.date || "",
        deliveryProtocolScores: {
          deliveryPictures: item.delivery_pictures || "",
          deliveryNotes: item.delivery_notes || "",
          instructionCompliance: item.instruction_compliance || "",
          deliverySuccess: item.delivery_success_score || "",
        },
        roadSafetyScores: {
          drivingBehaviour: item.driving_behaviour || "",
          vehicleHandling: item.vehicle_handling || "",
          safety: item.safety_score,
        },
        customerInteractionScores: {
          customerFeedback: item.customer_feedback || "",
          issueResolution: item.issue_resolution || "",
        },
        comments: item.comments || "",
        // Additional fields
        timeManagement: item.time_management || "",
        loadCheck: item.load_check || "",
      }))

      return transformedData
    },
  })
}
